import React from "react";
import Typography from "@material-ui/core/Typography";
import classnames from "classnames";
import { useFootballContext } from "../../../state/football-play/context";
import { usePlayersContext } from "../../../state/players/context";
import { useTeamsContext } from "../../../state/teams/context";
import driveReplayDialogStyles, {
	REPLAY_COLORS
} from "../styles/DriveReplayDialog.styles";

const playerLabel = (player) => {
	const name = [player.firstName?.[0], player.lastName]
		.filter(Boolean)
		.join(". ");

	return [player.uniform ? `#${player.uniform}` : null, name || player.lastName]
		.filter(Boolean)
		.join(" ");
};

const categoryLabel = (category) =>
	category.replace(/^./, (c) => c.toUpperCase());

const TeamColumn = ({ team, rows, classes }) => (
	<div className={classes.playerYardsTeam}>
		<Typography variant="caption" color="textSecondary">
			{team?.abbrName || team?.teamName || ""}
		</Typography>
		{rows.map((row) => (
			<div key={row.key} className={classes.playerYardsRow}>
				<span className={classes.playerYardsName}>{row.name}</span>
				<span className={classes.playerYardsCategory}>
					{categoryLabel(row.category)}
				</span>
				<span
					className={classes.playerYardsValue}
					style={{
						color: row.yards < 0 ? REPLAY_COLORS.loss : REPLAY_COLORS.gain
					}}
				>
					{`${row.yards > 0 ? "+" : ""}${row.yards}`}
				</span>
			</div>
		))}
	</div>
);

/**
 * What each player gained or lost on this drive, from the stat records the
 * rules engine wrote -- not summed off the field path, because the engine is
 * the authority on who is credited what (a completion is yards for the passer
 * AND the receiver; a sack is yards against).
 *
 * Every record carrying a player and a yards value is counted, so a category
 * added to the engine later shows up here without a change.
 */
const DriveReplayPlayerYards = () => {
	const classes = driveReplayDialogStyles();
	const { state } = useFootballContext();
	const { state: playersState } = usePlayersContext();
	const { state: teamState } = useTeamsContext();

	// Defaulted: a store built before this slice existed (a hot reload mid
	// session) hands back undefined rather than an empty list.
	const stats = state.driveReplayStats() || [];
	const roster = playersState.allPlayers() || [];
	const homeTeam = teamState.homeTeam();
	const awayTeam = teamState.awayTeam();

	const totals = new Map();

	stats
		.filter((s) => s.playerId && Number.isFinite(s.values?.yards))
		.forEach((s) => {
			const key = `${s.playerId}|${s.category}`;
			totals.set(key, (totals.get(key) || 0) + s.values.yards);
		});

	const rows = [...totals.entries()]
		.map(([key, yards]) => {
			const [playerId, category] = key.split("|");
			const player = roster.find((p) => p.id === playerId);

			return {
				key,
				name: player ? playerLabel(player) : "Unknown player",
				teamId: player?.teamId,
				category,
				yards
			};
		})
		.sort((a, b) => Math.abs(b.yards) - Math.abs(a.yards));

	// Rendered even when empty so the row keeps its shape -- the panel sharing
	// its row should not move because a drive has no yardage yet.
	return (
		<div className={classnames(classes.card, classes.playerYards)}>
			<Typography variant="caption" color="textSecondary">
				Yards by player on this drive
			</Typography>
			<div className={classes.playerYardsTeams}>
				<TeamColumn
					team={awayTeam}
					rows={rows.filter((r) => r.teamId === awayTeam?.id)}
					classes={classes}
				/>
				<TeamColumn
					team={homeTeam}
					rows={rows.filter((r) => r.teamId === homeTeam?.id)}
					classes={classes}
				/>
			</div>
		</div>
	);
};

export default DriveReplayPlayerYards;
